import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PageProps } from "../types/auth";
import { trackPageVisit } from "../utils/pageTracker";

import styles from './styles/CurriculumDetailsPage.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Level {
  level_id: number;
  name: string;
  sequence: number;
}

interface Curriculum {
  curriculum_id: number;
  name: string;
  levels_sequence: Level[];
}

interface RouteParams {
  [key: string]: string | undefined;
  curriculum_id?: string;
}

const CurriculumDetailsPage: React.FC<PageProps> = ({ token, selectedOrgID }) => {
  const { curriculum_id } = useParams<RouteParams>();
  const [curriculum, setCurriculum] = useState<Curriculum | null>(null);
  const [error, setError] = useState<string>("");
  const [levels, setLevels] = useState<Level[]>([]);
  const [name, setName] = useState<string>("");
  const navigate = useNavigate();
  const hasTrackedRef = useRef(false);

  // Track page visit once we have the curriculum name
  useEffect(() => {
    if (name && !hasTrackedRef.current) {
      trackPageVisit(
        curriculum_id ? `Curriculum: ${name}` : "New Curriculum",
        curriculum_id ? `/curriculums/${curriculum_id}` : "/curriculums/create"
      );
      hasTrackedRef.current = true;
    }
  }, [name, curriculum_id]);

  useEffect(() => {
    const fetchCurriculumDetails = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/curriculums/${curriculum_id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.error || "Failed to fetch curriculum details.");
          return;
        }

        const data: Curriculum = await response.json();
        setCurriculum(data);
        setLevels(data.levels_sequence || []);
        setName(data.name || "");
      } catch (err) {
        setError("An unexpected error occurred.");
      }
    };

    if (curriculum_id) {
      fetchCurriculumDetails();
    }
  }, [curriculum_id, token, selectedOrgID]);

  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  const onDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    dragItem.current = index;
    e.currentTarget.classList.add(styles.dragging);
  };

  const onDragEnter = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    dragOverItem.current = index;
  };

  const onDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    handleReorder();
    e.currentTarget.classList.remove(styles.dragging);
  };

  const onTouchStart = (e: React.TouchEvent<HTMLDivElement>, index: number) => {
    dragItem.current = index;
    touchStartY.current = e.touches[0].clientY;
    e.currentTarget.classList.add(styles.dragging);
  };

  const onTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    e.preventDefault();
    const touch = e.touches[0];
    const elements = document.elementsFromPoint(touch.clientX, touch.clientY);
    const levelItem = elements.find(el => el.classList.contains(styles.levelItem));
    
    if (levelItem) {
      const items = Array.from(document.getElementsByClassName(styles.levelItem));
      const overIndex = items.indexOf(levelItem);
      if (overIndex !== -1) {
        dragOverItem.current = overIndex;
      }
    }
  };

  const onTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    handleReorder();
    e.currentTarget.classList.remove(styles.dragging);
    touchStartY.current = null;
  };

  const handleReorder = () => {
    if (dragItem.current === null || dragOverItem.current === null) return;

    const tempLevels = [...levels];
    const draggedItemContent = tempLevels.splice(dragItem.current, 1)[0];
    tempLevels.splice(dragOverItem.current, 0, draggedItemContent);

    dragItem.current = null;
    dragOverItem.current = null;

    setLevels(tempLevels);
  };

  const handleSave = async () => {
    try {
      const updatedLevelsSequence = levels.map((level, index) => ({
        ...level,
        sequence: index + 1,
      }));

      const response = await fetch(`${API_BASE_URL}/api/curriculums/${curriculum_id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
        body: JSON.stringify({
          name,
          levels_sequence: updatedLevelsSequence,
        }),
      });

      if (!response.ok) {
        setError("Failed to save curriculum.");
        return;
      }

      navigate("/curriculums");
    } catch (err) {
      setError("An unexpected error occurred.");
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/curriculums/${curriculum_id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
      });

      if (!response.ok) {
        setError("Failed to delete curriculum.");
        return;
      }

      navigate("/curriculums");
    } catch (err) {
      setError("An unexpected error occurred.");
    }
  };

  if (error) {
    return <p className={styles.errorMessage}>{error}</p>;
  }

  if (!curriculum) {
    return <div>Loading...</div>;
  }

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Curriculum Details</h1>
      <div className={styles.formGroup}>
        <div className={styles.inputGroup}>
          <label className={styles.label}>Name:</label>
          <input
            type="text"
            value={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            className={styles.input}
          />
        </div>
      </div>

      <div className={styles.mainActions}>
        <button onClick={handleSave} className={styles.button}>
          Save
        </button>
        <button onClick={handleDelete} className={styles.deleteButton}>
          Delete Curriculum
        </button>
      </div>

      <div className={styles.levelsSection}>
        <h2 className={styles.levelsTitle}>Levels</h2>
        <div className={styles.newLevelContainer}>
          <button
            onClick={() =>
              navigate("/levels/create", {
                state: {
                  curriculum_id,
                },
              })
            }
            className={styles.newLevelButton}
          >
            New Level
          </button>
        </div>

        <div className={styles.levelsList}>
          {levels.map((level, index) => (
            <div
              key={level.level_id}
              className={`${styles.levelItem} ${dragItem.current === index ? styles.dragging : ''}`}
              draggable
              onDragStart={(e) => onDragStart(e, index)}
              onDragEnter={(e) => onDragEnter(e, index)}
              onDragEnd={onDragEnd}
              onDragOver={(e) => e.preventDefault()}
              onTouchStart={(e) => onTouchStart(e, index)}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              <a
                href="#"
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  e.preventDefault();
                  navigate(`/levels/${level.level_id}`, {
                    state: {
                      curriculum_id,
                    }
                  });
                }}
                className={styles.levelLink}
              >
                {index + 1} {level.name}
              </a>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CurriculumDetailsPage;
